import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navigation = [
  { name: "Accueil", href: "/" },
  { name: "À propos", href: "/a-propos" },
  { name: "Recherches", href: "/recherches" },
  { name: "Ventes", href: "/ventes" },
  { name: "Valorisation", href: "/valorisation" },
  { name: "Honoraires", href: "/honoraires" },
  { name: "Nos trouvailles", href: "/nos-trouvailles" },
  { name: "FAQ", href: "/faq" },
  { name: "Contact", href: "/contact" },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/98 backdrop-blur-xl shadow-sm"
          : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      {/* Logo band - white background */}
      <div className="bg-white py-2 border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex justify-center">
          <Link to="/" className="flex items-center justify-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_vente-achat-paris/artifacts/81fllxqd_logo%20site.png"
              alt="Emplacement-Immo — Recherches & Ventes"
              className="h-[72px] w-auto object-contain"
              data-testid="header-logo-img"
            />
          </Link>
        </div>
      </div>

      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-semibold transition-colors duration-300 ${
                  location.pathname === item.href
                    ? "text-gold"
                    : "text-text-primary hover:text-gold"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-text-primary hover:text-gold transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`block py-2 text-base font-semibold transition-colors duration-300 ${
                      location.pathname === item.href
                        ? "text-gold"
                        : "text-text-primary hover:text-gold"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
